import { baza } from "@/lib/baza";
import { dohvatiSate, type ZapisSati } from "@/lib/sati";

/**
 * What the server has already accepted for one site on one day, kept on the
 * device — O-19.
 *
 * This exists because of what happened on the first physical test. The entry
 * screen used to read the day's accepted hours from the network only, and to
 * fall back to an empty list when that failed. With no signal the crew
 * therefore looked untouched: three workers whose hours had been recorded and
 * accepted an hour earlier were offered a "Potvrdi" button again. The hours
 * were entered a second time, the server refused the duplicates under the
 * twelve-hour rule, and five refused entries were left stuck on the phone.
 *
 * So the rule is now the opposite of what it was: **the screen never shows
 * empty because a request failed.** It shows the last answer this phone got,
 * and the queue on top of it.
 *
 * The server stays the source of truth. This is a copy and may be thrown away
 * at any time — unlike the queue next to it, which exists nowhere else. A copy
 * can be out of date; what it can never do is claim less than it knows, which
 * is the failure that costs somebody an evening of re-entry.
 */

export interface SnimljeniPoslani {
  gradilisteId: string;
  /** The day these entries are for, ISO. */
  datum: string;
  zapisi: ZapisSati[];
  /** When this answer was taken, ISO. */
  osvjezeno: string;
}

/**
 * Stored per site **and** day.
 *
 * Both, because a manager with two sites moves between them on the same day
 * and may enter a day he missed on either — one slot per day would show one
 * site's recorded hours under the other's heading, which is the same class of
 * mistake this whole module exists to stop.
 */
function kljuc(gradilisteId: string, datum: string): string {
  return `${gradilisteId}|${datum}`;
}

export async function spremiPoslano(snimka: SnimljeniPoslani): Promise<void> {
  await (
    await baza()
  ).put("poslano", snimka, kljuc(snimka.gradilisteId, snimka.datum));
}

/**
 * The stored answer, or `null` when this phone has never had one.
 *
 * `null` and an empty list are different facts and only one of them means
 * "nothing is recorded for this day": the other means "this phone has not been
 * online since the day started". The screen has to be able to tell them apart.
 */
export async function procitajPoslano(
  gradilisteId: string,
  datum: string,
): Promise<ZapisSati[] | null> {
  const snimka = await (await baza()).get("poslano", kljuc(gradilisteId, datum));
  return snimka?.zapisi ?? null;
}

/**
 * Ask the server and keep the answer.
 *
 * Throws when the network is not there, and the caller keeps showing the
 * stored copy. Not caught here on purpose: this function's job is to refresh,
 * and a refresh that quietly succeeds by returning yesterday's answer is how a
 * screen ends up lying about which day it is showing.
 */
export async function osvjeziPoslano(
  gradilisteId: string,
  datum: string,
  signal?: AbortSignal,
): Promise<ZapisSati[]> {
  const zapisi = await dohvatiSate(gradilisteId, datum, signal);
  await spremiPoslano({
    gradilisteId,
    datum,
    zapisi,
    osvjezeno: new Date().toISOString(),
  });
  return zapisi;
}
